import React from 'react';
import { Node } from 'reactflow';
import { TaxonomyStats } from '../../types/taxonomy';
import { Badge } from '../ui/badge';
import { Timestamp } from '../ui/timestamp';

interface TaxonomyStatsPanelProps {
  stats: TaxonomyStats | null;
  selectedNodeId: string | null;
  nodes: Node[];
  taxonomyData: any;
  signalClassification?: any;
  highlightSignal?: string;
}

const TaxonomyStatsPanel: React.FC<TaxonomyStatsPanelProps> = ({
  stats,
  selectedNodeId,
  nodes,
  taxonomyData,
  signalClassification,
  highlightSignal,
}) => {
  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };
  
  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`;
  };
  
  const countDetectedLabelsInSignal = (signalClassification: any) => {
    if (!signalClassification?.dimensions) return 0;
    
    let count = 0;
    Object.values(signalClassification.dimensions).forEach((dimension: any) => {
      if (dimension.categories) {
        Object.values(dimension.categories).forEach((category: any) => {
          if (category.labels) {
            count += Object.keys(category.labels).length;
          }
        });
      }
    });
    return count;
  };

  const getSignalDetectedLabels = (signalClassification: any) => {
    if (!signalClassification?.dimensions) return [];
    
    const labels: any[] = [];
    Object.entries(signalClassification.dimensions).forEach(([dimensionId, dimension]: [string, any]) => {
      if (dimension.categories) {
        Object.entries(dimension.categories).forEach(([categoryId, category]: [string, any]) => {
          if (category.labels) {
            Object.entries(category.labels).forEach(([labelId, label]: [string, any]) => {
              labels.push({
                label_id: labelId,
                label_name: label.label_name,
                confidence: label.confidence,
                dimension_name: dimension.dimension_name,
                category_name: category.category_name,
                rationale: label.rationale
              });
            });
          }
        });
      }
    });
    return labels.sort((a, b) => b.confidence - a.confidence);
  };

  return (
    <div className="space-y-5 p-4">
      <section className="rounded-xl border border-border bg-background p-4 shadow-[var(--shadow-card)]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
              {highlightSignal ? 'Signal analysis' : 'Overview stats'}
            </h2>
            <p className="text-[13px] leading-[1.7] text-muted-foreground">
              Taxonomy coverage and detection totals.
            </p>
          </div>
          <Badge variant={highlightSignal ? 'default' : 'secondary'}>
            {highlightSignal ? 'Focused' : 'Global'}
          </Badge>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Metric label="Total labels" value={formatNumber(taxonomyData?.totalLabels || 0)} />
          <Metric
            label="Detected labels"
            value={formatNumber(
              highlightSignal
                ? signalClassification
                  ? countDetectedLabelsInSignal(signalClassification)
                  : 0
                : stats?.top_detected_categories?.length || 0
            )}
          />
          <Metric
            label="Total signals"
            value={highlightSignal ? '1' : formatNumber(stats?.total_signals || 0)}
          />
          <Metric
            label="Last update"
            value={stats?.last_classification_run ? <Timestamp value={stats.last_classification_run} format="absolute" /> : 'N/A'}
          />
        </div>
      </section>

      {selectedNode && (
        <section className="rounded-xl border border-border bg-accent/30 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
                Selected node
              </h2>
              <p className="text-[13px] leading-[1.7] text-muted-foreground">Current graph focus.</p>
            </div>
            <Badge variant="outline">{selectedNode.data.type}</Badge>
          </div>

          <div className="space-y-3">
            <div>
              <div className="text-[13px] font-semibold text-foreground">{selectedNode.data.label}</div>
              {selectedNode.data.description && (
                <div className="mt-1 text-[13px] leading-[1.7] text-muted-foreground">
                  {selectedNode.data.description}
                </div>
              )}
            </div>

            {selectedNode.data.detected && (
              <div className="space-y-2 rounded-lg border border-border bg-background p-3">
                <Row label="Detection status" value={<Badge variant="success">Detected</Badge>} />
                {selectedNode.data.confidence !== undefined && (
                  <Row label="Confidence" value={formatPercentage(selectedNode.data.confidence * 100)} />
                )}
                {selectedNode.data.signalCount !== undefined && (
                  <Row label="Signal count" value={selectedNode.data.signalCount} />
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {(highlightSignal ? signalClassification : stats?.top_detected_categories?.length) && (
        <section className="rounded-xl border border-border bg-background p-4 shadow-[var(--shadow-card)]">
          <div className="mb-4">
            <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
              {highlightSignal ? 'Detected labels' : 'Top detected labels'}
            </h2>
            <p className="text-[13px] leading-[1.7] text-muted-foreground">
              Most relevant labels surfaced by the current taxonomy view.
            </p>
          </div>

          <div className="space-y-3">
            {highlightSignal && signalClassification ? (
              getSignalDetectedLabels(signalClassification).map((label: any) => (
                <div key={label.label_id} className="rounded-lg border border-border bg-muted/20 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[13px] font-semibold text-foreground">{label.label_name}</div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                        {label.dimension_name} → {label.category_name}
                      </div>
                    </div>
                    <Badge variant="outline">{formatPercentage(label.confidence * 100)}</Badge>
                  </div>
                  {label.rationale && (
                    <div className="mt-2 text-[13px] leading-[1.7] text-muted-foreground">
                      {label.rationale}
                    </div>
                  )}
                </div>
              ))
            ) : (
              stats?.top_detected_categories?.slice(0, 5).map((category: any) => (
                <div key={category.label_id || category.category_id} className="rounded-lg border border-border bg-muted/20 p-3">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="text-[13px] font-semibold text-foreground">
                      {category.label_name || category.name}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                      {formatNumber(category.count)}
                    </div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${category.percentage}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {stats?.confidence_distribution && (
        <section className="rounded-xl border border-border bg-background p-4 shadow-[var(--shadow-card)]">
          <div className="mb-4">
            <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">
              Confidence distribution
            </h2>
            <p className="text-[13px] leading-[1.7] text-muted-foreground">
              Confidence levels across detected labels.
            </p>
          </div>

          <div className="space-y-3">
            <Row label="High (&gt;80%)" value={formatNumber(stats.confidence_distribution.high)} tone="success" />
            <Row label="Medium (50-80%)" value={formatNumber(stats.confidence_distribution.medium)} tone="attention" />
            <Row label="Low (&lt;50%)" value={formatNumber(stats.confidence_distribution.low)} tone="muted" />
          </div>
        </section>
      )}

      <section className="rounded-xl border border-border bg-background p-4 shadow-[var(--shadow-card)]">
        <div className="mb-4">
          <h2 className="font-jakarta text-[15px] font-bold tracking-[-0.01em] text-foreground">Legend</h2>
          <p className="text-[13px] leading-[1.7] text-muted-foreground">Node colors used in the graph.</p>
        </div>
        <div className="space-y-2 text-[13px] leading-[1.7] text-muted-foreground">
          <LegendRow colorClass="bg-foreground" label="Root node" />
          <LegendRow colorClass="bg-primary" label="Dimension" />
          <LegendRow colorClass="bg-[linear-gradient(135deg,rgba(146,78,255,0.96),rgba(67,66,245,0.96))]" label="Category" />
          <LegendRow colorClass="bg-background border border-border" label="Undetected label" />
          <LegendRow colorClass="bg-[color:rgba(157,11,77,0.06)] border border-[var(--go-berry)]/20" label="Detected label" />
        </div>
      </section>
    </div>
  );
};

export default TaxonomyStatsPanel;

const Metric: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="rounded-lg border border-border bg-background px-3 py-3">
    <div className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">{label}</div>
    <div className="mt-1 text-[15px] font-semibold tracking-[-0.01em] text-foreground">{value}</div>
  </div>
);

const Row: React.FC<{ label: string; value: React.ReactNode; tone?: 'success' | 'attention' | 'muted' }> = ({
  label,
  value,
  tone = 'muted',
}) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2">
    <span className="text-[13px] text-muted-foreground">{label}</span>
    <span
      className={
        tone === 'success'
          ? 'text-[13px] font-semibold text-[var(--go-success)]'
          : tone === 'attention'
            ? 'text-[13px] font-semibold text-[var(--go-bittersweet)]'
            : 'text-[13px] font-semibold text-foreground'
      }
    >
      {value}
    </span>
  </div>
);

const LegendRow: React.FC<{ colorClass: string; label: string }> = ({ colorClass, label }) => (
  <div className="flex items-center gap-2">
    <span className={`h-4 w-4 rounded-full ${colorClass}`} />
    <span>{label}</span>
  </div>
);
